/**
 * Author: Srilal S. Siriwardhane
 * Email: SrilalS@99x.io
**/
import { FilesetResolver, LlmInference } from "@mediapipe/tasks-genai";
import { get, set } from 'idb-keyval';
import { AbstractLLMInferenceEngine } from "./AbstractLLMInferenceEngine.js";
import { ModelInfo } from "../types/Model.js";
import axios from 'axios';
export class MediaPipeInferenceEngine extends AbstractLLMInferenceEngine {
    status;
    localMode = false;
    llmModel;
    llmInference;
    availableModels = {
        'gemma-cpu': new ModelInfo('gemma-2b-it-cpu-int4.bin'),
        'gemma-gpu': new ModelInfo('gemma-2b-it-gpu-int4.bin')
    };
    /**
     *
     * @param model - The model to be used for the inference. must be of type `MediaPipeModel`. Currently
     * supported models are `gemma-cpu` and `gemma-gpu`.
     * @param localMode - The mode to be used for the inference. Default is `false`. set to `true` if you are
     * running a model hosting server locally.
     */
    constructor(model, localMode = false) {
        super();
        this.status = {
            progress: 0,
            text: '[ Initializing ]',
            timeElapsed: 0
        };
        console.log('Mode:' + localMode ? 'Local' : 'Remote');
        this.localMode = localMode;
        this.llmModel = model;
    }
    /**
     * Async function to initialize the MediaPipeInferenceEngine.
     * @param model - The model to be used for the inference. must be of type `MediaPipeModel`. Currently
     * supported models are `gemma-cpu` and `gemma-gpu`.
     * @param localMode - The mode to be used for the inference. Default is `false`. set to `true` if you are
     * running a model hosting server locally.
     * @param maxTokens - The maximum number of tokens to be used for the inference. Default is `512`.
     * @param topK - The number of top K tokens to be used for the inference. Default is `1`.
     * @param temperature - The temperature to be used for the inference. Default is `0.0`.
     * @param randomSeed - The random seed to be used for the inference. Default is `1`.
     */
    static async init(model, localMode = false, maxTokens = 512, topK = 1, temperature = 0.0, randomSeed = 1) {
        const instance = new MediaPipeInferenceEngine(model, localMode);
        await instance.createEngine(maxTokens, topK, temperature, randomSeed);
        return instance;
    }
    /**
     * createEngine - Async function to create the MediaPipeInferenceEngine.
     * @param maxTokens - The maximum number of tokens to be used for the inference. Default is `512`.
     * @param topK - The number of top K tokens to be used for the inference. Default is `1`.
     * @param temperature - The temperature to be used for the inference. Default is `0.0`.
     * @param randomSeed - The random seed to be used for the inference. Default is `1`.
     */
    async createEngine(maxTokens = 512, topK = 1, temperature = 0.0, randomSeed = 1) {
        const genai = await FilesetResolver.forGenAiTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-genai/wasm");
        const gpu = await LlmInference.createWebGpuDevice();
        this.llmInference = await LlmInference.createFromOptions(genai, {
            baseOptions: {
                modelAssetPath: await this.restoreFileFromIDB(),
                gpuOptions: {
                    device: gpu
                }
            },
            maxTokens: maxTokens,
            topK: topK,
            temperature: temperature,
            randomSeed: randomSeed,
        });
    }
    /**
     * downloadModel - Async function to download the model from the server.
     */
    async downloadModel() {
        const localURL = "http://127.0.0.1:8000/models/mp-gemma/gemma-2b-it-gpu-int4.bin";
        const remoteURL = "https://storage.googleapis.com/jmstore/kaggleweb/grader/g-2b-it-gpu-int4.bin";
        this.status = {
            progress: 0,
            text: '[ Downloading Model ]',
            timeElapsed: 0
        };
        const url = this.localMode ? localURL : remoteURL;
        const startTime = Date.now();
        const response = await axios.get(url, {
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const timeElapsed = (Date.now() - startTime) / 1000;
                console.log('Model Download Progress: ' + progress + '%');
                console.log('Time Elapsed: ' + timeElapsed + 's');
            },
        });
        const blob = response.data;
        console.log('Model Download Progress: ' + 100 + '%');
        await this.storeFileInIDB(blob);
    }
    async storeFileInIDB(blob) {
        const start = performance.now();
        await set('gemma-2b-it-gpu-int4.bin', blob);
        const end = performance.now();
        console.log('Model file cached in IDB.' + (end - start) / 1000 + 's');
    }
    ;
    async restoreFileFromIDB() {
        const start = performance.now();
        const file = await get(this.getModel());
        if (!file) {
            console.log('Model file not found in IDB. Downloading...');
            await this.downloadModel();
            return this.restoreFileFromIDB();
        }
        const end = performance.now();
        console.log('Cached model file found in IDB.' + (end - start) / 1000 + 's');
        return URL.createObjectURL(file);
    }
    ;
    async clearIDB() {
        await set(this.getModel(), null);
        await set(this.getModel(), null);
    }
    async runChatInference(chat) {
        throw new Error("Method not supported on this LlmInference Engine. Use runCompletionInference instead.");
    }
    async runCompletionInference(prompt) {
        const response = await this.llmInference.generateResponse(prompt);
        return response;
    }
    countCharacterUsingRegex(str, char) {
        const regex = new RegExp(char, 'g');
        const matches = str.match(regex);
        return matches ? matches.length : 0;
    }
    async runJSONInference(prompt) {
        let modelOutput = "";
        let validJSONOutput = "";
        let openBracketCount = (modelOutput.match(/{/g) || []).length;
        let closeBracketCount = (modelOutput.match(/}/g) || []).length;
        return new Promise((resolve, reject) => {
            this.llmInference.generateResponse(prompt, (partial, done) => {
                console.log('Partial: ' + partial);
                modelOutput += partial;
                openBracketCount += this.countCharacterUsingRegex(partial, '{');
                closeBracketCount += this.countCharacterUsingRegex(partial, '}');
                const breakCondition = (openBracketCount === closeBracketCount) && (openBracketCount > 0 && closeBracketCount > 0);
                if (breakCondition) {
                    validJSONOutput = modelOutput;
                    resolve(validJSONOutput + ' __END__');
                }
                if (done) {
                    resolve('{"error": "Invalid JSON"}');
                }
            });
        });
    }
    progressCallback(initProgress) { }
    /**
     * getModel
     * @returns string
     *
     * This function is used to get the model name from the available models.
     *
     * @throws Error - If the model is not found or not supported.
     * */
    getModel() {
        try {
            return this.availableModels[this.llmModel].name;
        }
        catch (error) {
            throw new Error("Model not Found or not Supported!");
        }
    }
}
//# sourceMappingURL=MediaPipeInferenceEngine.js.map